/**
 * Per-device personalization: a nickname, a named group (a place, a company, a client),
 * an icon and a colour. Servers report identity; people organize it. This local store
 * serves the installed SSH client and explicit legacy browser import. Browser accounts
 * use the shared preference contract and the authenticated server API.
 */
import type { RemoteDevice } from '../model.js';

import { deviceIcons, deviceColors, type DeviceCustomization } from '@atum-remote/preferences';
export { deviceIcons, deviceColors };
export type { DeviceCustomization };
export type DeviceIcon = (typeof deviceIcons)[number];
export const deviceIconLabel: Readonly<Record<DeviceIcon, string>> = {
  monitor: 'Desktop',
  tower: 'Tower',
  laptop: 'Laptop',
  server: 'Server',
  nas: 'Storage',
  mini: 'Mini PC',
  gpu: 'Workstation',
  tv: 'Display',
  tablet: 'Tablet',
  phone: 'Phone',
  cloud: 'Virtual machine',
  gamepad: 'Gaming PC',
  router: 'Network',
};

/** Identity colours only. State always uses the tone palette, never these. */
export type DeviceColor = (typeof deviceColors)[number];
export const deviceColorLabel: Readonly<Record<DeviceColor, string>> = {
  mint: 'Mint',
  sky: 'Sky',
  violet: 'Violet',
  rose: 'Rose',
  lime: 'Lime',
  slate: 'Slate',
};

export type CustomizationMap = Readonly<Record<string, DeviceCustomization>>;

export type DirectoryView = 'grid' | 'list';
export type DirectorySort = 'name' | 'status' | 'seen';
export interface DirectoryPreferences {
  readonly view: DirectoryView;
  readonly sort: DirectorySort;
  readonly collapsed: readonly string[];
}

export const customizationKey = 'atum-remote.device-customization.v1';
export const directoryKey = 'atum-remote.directory.v1';
export const nameLimit = 80;
export const groupLimit = 60;
const entryLimit = 500;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function isIcon(value: unknown): value is DeviceIcon {
  return typeof value === 'string' && (deviceIcons as readonly string[]).includes(value);
}
function isColor(value: unknown): value is DeviceColor {
  return typeof value === 'string' && (deviceColors as readonly string[]).includes(value);
}
function text(value: unknown, limit: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().replace(/\s+/g, ' ');
  return trimmed ? trimmed.slice(0, limit) : undefined;
}

/** Drops unknown keys, invalid values and empty strings. Returns undefined when nothing remains. */
export function normalizeCustomization(input: unknown): DeviceCustomization | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const record = input as Record<string, unknown>;
  const name = text(record.name, nameLimit);
  const group = text(record.group, groupLimit);
  const result: DeviceCustomization = {
    ...(name ? { name } : {}),
    ...(group ? { group } : {}),
    ...(isIcon(record.icon) ? { icon: record.icon } : {}),
    ...(isColor(record.color) ? { color: record.color } : {}),
  };
  return Object.keys(result).length ? result : undefined;
}

function readJson(storage: StorageLike | undefined, key: string): unknown {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}
function writeJson(storage: StorageLike | undefined, key: string, value: unknown): void {
  try {
    if (!storage) throw new Error('Storage unavailable');
    storage.setItem(key, JSON.stringify(value));
  } catch {
    throw new Error(
      'Changes could not be saved on this device. Local storage is unavailable or full.',
    );
  }
}

export function readCustomizations(storage: StorageLike | undefined): CustomizationMap {
  const raw = readJson(storage, customizationKey);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const result: Record<string, DeviceCustomization> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>).slice(0, entryLimit)) {
    const normalized = normalizeCustomization(value);
    if (normalized && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(id)) result[id] = normalized;
  }
  return result;
}

/**
 * Merges a patch into one device's customization. Fields set to an empty string or
 * undefined are cleared; a device with nothing left is removed from the map.
 */
export function writeCustomization(
  storage: StorageLike | undefined,
  id: string,
  patch: { readonly [K in keyof DeviceCustomization]?: DeviceCustomization[K] | '' },
): CustomizationMap {
  const current = readCustomizations(storage);
  const merged = normalizeCustomization({ ...current[id], ...patch });
  const next: Record<string, DeviceCustomization> = { ...current };
  if (merged) next[id] = merged;
  else delete next[id];
  if (Object.keys(next).length > entryLimit)
    throw new Error(`Up to ${entryLimit} devices can be customized on this device.`);
  writeJson(storage, customizationKey, next);
  return next;
}

export function clearCustomization(storage: StorageLike | undefined, id: string): CustomizationMap {
  const next: Record<string, DeviceCustomization> = { ...readCustomizations(storage) };
  delete next[id];
  writeJson(storage, customizationKey, next);
  return next;
}

/** Effective group: the person's label wins over what the adapter reported. */
export function effectiveGroup(
  device: RemoteDevice,
  customization: DeviceCustomization | undefined,
): string {
  return customization?.group || device.group;
}

/** Moves every device shown under `from` into `to`, clearing overrides that match the reported group. */
export function renameGroup(
  storage: StorageLike | undefined,
  from: string,
  to: string,
  devices: readonly RemoteDevice[],
): CustomizationMap {
  const target = text(to, groupLimit);
  if (!target) throw new Error('Enter a group name.');
  const map: Record<string, DeviceCustomization> = { ...readCustomizations(storage) };
  for (const device of devices) {
    if (effectiveGroup(device, map[device.id]) !== from) continue;
    const merged = normalizeCustomization({
      ...map[device.id],
      group: target === device.group ? '' : target,
    });
    if (merged) map[device.id] = merged;
    else delete map[device.id];
  }
  if (Object.keys(map).length > entryLimit)
    throw new Error(`Up to ${entryLimit} devices can be customized on this device.`);
  // Commit the complete group together; failures must not leave half of it renamed.
  writeJson(storage, customizationKey, map);
  return map;
}

const defaultPreferences: DirectoryPreferences = { view: 'grid', sort: 'name', collapsed: [] };

export function readDirectoryPreferences(storage: StorageLike | undefined): DirectoryPreferences {
  const raw = readJson(storage, directoryKey);
  if (!raw || typeof raw !== 'object') return defaultPreferences;
  const record = raw as Record<string, unknown>;
  return {
    view: record.view === 'list' ? 'list' : 'grid',
    sort: record.sort === 'status' || record.sort === 'seen' ? record.sort : 'name',
    collapsed: Array.isArray(record.collapsed)
      ? record.collapsed.filter((item): item is string => typeof item === 'string').slice(0, 200)
      : [],
  };
}

export function writeDirectoryPreferences(
  storage: StorageLike | undefined,
  patch: Partial<DirectoryPreferences>,
): DirectoryPreferences {
  const next = { ...readDirectoryPreferences(storage), ...patch };
  writeJson(storage, directoryKey, next);
  return next;
}
